import { useCallback, useMemo, useSyncExternalStore } from "react";

function subscribe(callback: () => void) {
  window.addEventListener("local-storage", callback);
  window.addEventListener("storage", callback);
  return () => {
    window.removeEventListener("local-storage", callback);
    window.removeEventListener("storage", callback);
  };
}

// Cache to store the last raw string and parsed value per key to avoid re-parsing
const cache = new Map<string, { raw: string | null; parsed: unknown }>();

function getValue(key: string) {
  const item = localStorage.getItem(key);
  const cached = cache.get(key);

  // If the raw string hasn't changed, return cached parsed value
  if (cached && cached.raw === item) {
    return cached.parsed;
  }

  // Parse and cache the new value
  let parsed: unknown = null;
  if (item) {
    try {
      parsed = JSON.parse(item);
    } catch (err) {
      parsed = null;
    }
  }

  cache.set(key, { raw: item, parsed });
  return parsed;
}

/**
 * useLocalStorage
 *
 * A wrapper around React state that:
 * - Initializes from localStorage (JSON.parse) with a provided default
 * - Persists every change back to localStorage (JSON.stringify)
 * - Synchronizes multiple hook instances in the same tab via a custom event
 * - Synchronizes across browser tabs via the storage event
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  // Memoize getSnapshot to avoid infinite callbacks
  const getSnapshot = useMemo(() => () => getValue(key), [key]);

  const value = useSyncExternalStore(subscribe, getSnapshot, getSnapshot) as T | null;

  // Use useCallback for referential equality when passed down
  const setValue = useCallback(
    (updater: React.SetStateAction<T>) => {
      const currentValue = value ?? initialValue;
      const newValue =
        typeof updater === "function" ? (updater as (current: T) => T)(currentValue) : updater;

      localStorage.setItem(key, JSON.stringify(newValue));
      window.dispatchEvent(new CustomEvent("local-storage"));
    },
    [key, value, initialValue],
  );

  return [value ?? initialValue, setValue];
}
